import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api/client.js';
import { LeaderboardEntry } from '../types/index.js';
import './AdminPage.css';

const STORYLINE_NAMES: Record<string, string> = {
  ghostpay: 'GhostPay',
  identityshift: 'IdentityShift',
  shadowmerchant: 'ShadowMerchant',
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', maximumFractionDigits: 0,
  }).format(n);
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function downloadCSV(entries: LeaderboardEntry[]) {
  const cols = ['Date', 'Name', 'Email', 'Score', 'Fraud Prevented (AUD)', 'Storyline', 'Timestamp'];
  const rows = entries.map(e => [
    e.date,
    e.playerName,
    e.playerEmail ?? '',
    e.score,
    e.fraudPrevented,
    STORYLINE_NAMES[e.storylineId] ?? e.storylineId,
    e.timestamp,
  ]);
  const csv = [cols, ...rows]
    .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fraud-or-not-export-${todayISO()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Winner slot-machine animation ─────────────────────────────────────────────
// Pre-selects the winner immediately; spins through random names for ~2.5s then
// lands on the chosen winner.
function useWinnerPicker(entries: LeaderboardEntry[]) {
  const [spinning, setSpinning] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [winner, setWinner] = useState<LeaderboardEntry | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pick = () => {
    if (entries.length === 0 || spinning) return;

    // Pick winner upfront (pure random — even odds regardless of score)
    const chosen = entries[Math.floor(Math.random() * entries.length)];
    setWinner(null);
    setSpinning(true);

    let elapsed = 0;

    // Phase 1: fast spin (50ms)
    intervalRef.current = setInterval(() => {
      const random = entries[Math.floor(Math.random() * entries.length)];
      setDisplayName(random.playerName);
      elapsed += 50;

      if (elapsed >= 1500) {
        // Phase 2: slow spin (200ms)
        clearInterval(intervalRef.current!);
        intervalRef.current = setInterval(() => {
          const random2 = entries[Math.floor(Math.random() * entries.length)];
          setDisplayName(random2.playerName);
          elapsed += 200;

          if (elapsed >= 2700) {
            clearInterval(intervalRef.current!);
            setDisplayName(chosen.playerName);
            setSpinning(false);
            setWinner(chosen);
          }
        }, 200);
      }
    }, 50);
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  return { pick, spinning, displayName, winner };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AdminPage() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem('admin-secret') ?? '');
  const [secretInput, setSecretInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [date, setDate] = useState(todayISO());
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const { pick, spinning, displayName, winner } = useWinnerPicker(entries);

  const authenticated = !!secret;

  // Load leaderboard whenever date or auth changes
  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    api.adminLeaderboard(secret, date)
      .then(r => setEntries(r.entries))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [date, secret, authenticated]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      await api.adminVerify(secretInput);
      sessionStorage.setItem('admin-secret', secretInput);
      setSecret(secretInput);
    } catch {
      setAuthError('Invalid passphrase');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const { entries: all } = await api.adminExport(secret);
      downloadCSV(all);
    } catch {
      alert('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin-secret');
    setSecret('');
    setSecretInput('');
  };

  // ── Auth prompt ──────────────────────────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="admin-page">
        <motion.div
          className="admin-auth"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2>🔐 Admin Access</h2>
          <p>Enter the admin passphrase to continue.</p>
          <form onSubmit={handleAuth}>
            <div className="admin-auth-row">
              <input
                type="password"
                placeholder="Passphrase"
                value={secretInput}
                onChange={e => setSecretInput(e.target.value)}
                autoFocus
              />
              <button
                type="submit"
                className="btn-admin btn-primary"
                disabled={!secretInput || authLoading}
              >
                {authLoading ? '…' : 'Unlock'}
              </button>
            </div>
            {authError && <p className="admin-auth-error">{authError}</p>}
          </form>
        </motion.div>
      </div>
    );
  }

  // ── Dashboard ────────────────────────────────────────────────────────────
  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Fraud or Not — Admin</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="admin-header-meta">Logged in</span>
          <button className="btn-admin btn-outline" onClick={handleLogout}>Log out</button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar">
        <label>
          Date
          <input
            type="date"
            value={date}
            max={todayISO()}
            onChange={e => setDate(e.target.value)}
          />
        </label>
        <button
          className="btn-admin btn-outline"
          onClick={handleExport}
          disabled={exportLoading}
        >
          {exportLoading ? 'Exporting…' : '📥 Export All Time CSV'}
        </button>
        <span className="admin-count">
          {loading ? 'Loading…' : `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`}
        </span>
      </div>

      {/* Leaderboard table */}
      <div className="admin-table-wrap">
        {entries.length === 0 && !loading ? (
          <p className="admin-empty">No entries for {date}</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th className="col-rank">#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Score</th>
                <th>Fraud Prevented</th>
                <th>Storyline</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={`${entry.playerName}-${entry.timestamp}`}>
                  <td className="col-rank">{entry.rank}</td>
                  <td>{entry.playerName}</td>
                  <td className="col-muted">{entry.playerEmail ?? '—'}</td>
                  <td className="col-score">{entry.score}/10</td>
                  <td className="col-prevented">{formatCurrency(entry.fraudPrevented)}</td>
                  <td className="col-muted">{STORYLINE_NAMES[entry.storylineId] ?? entry.storylineId}</td>
                  <td className="col-muted">{new Date(entry.timestamp).toLocaleTimeString('en-AU')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Winner picker */}
      <div className="admin-winner-section">
        <button
          className="btn-admin btn-success"
          onClick={pick}
          disabled={entries.length === 0 || spinning}
        >
          🎲 Pick a Winner
        </button>

        <AnimatePresence mode="wait">
          {(spinning || winner) && (
            <motion.div
              key={winner?.timestamp ?? 'spinning'}
              className="admin-winner-reveal"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="admin-winner-label">
                {spinning ? 'Selecting…' : '🏆 Winner'}
              </div>
              <div className={`admin-winner-name${spinning ? ' spinning' : ''}`}>
                {spinning ? displayName : winner?.playerName}
              </div>
              {!spinning && winner && (
                <div className="admin-winner-meta">
                  {winner.score}/10 · {STORYLINE_NAMES[winner.storylineId] ?? winner.storylineId}
                  {winner.playerEmail && <> · {winner.playerEmail}</>}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
